/**
 * Date utility functions
 * Simple implementations without external date library dependencies
 */

import { i18n } from '@/platform/i18n'
import { userConfigService } from '@/shared/services/user-config.service'
import { usableTimeZone, wallClockInZone } from '@/shared/utils/zoned-time'

// The active app language at CALL TIME (reflects live language switches), defaulting
// to 'en' when the i18next instance is not yet initialized. Intl.* accepts 'es'/'en'.
const appLocale = (): string => i18n?.language || 'en'

/**
 * ⚠️⚠️ THE one way to print an absolute date/time with `Intl` in this app: the caller's locale and options,
 * on the clock of the user's PROFILE zone (Preferencias → zona horaria, UTC by default).
 *
 * ~70 screens called `date.toLocaleDateString(i18n.language, {…})` directly, which prints on the BROWSER's
 * clock: with a Tokyo profile the ledger said a ticket was answered on 06/06 and the support page said 05/06.
 * An explicit `options.timeZone` still wins. An unparseable value prints '' — never «Invalid Date».
 */
export function formatDateInProfileZone(
  value: string | number | Date,
  locales: Intl.LocalesArgument,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(locales, {
    ...options,
    timeZone: options.timeZone ?? usableTimeZone(userConfigService.getUserTimezone()),
  }).format(date)
}

/**
 * Format a timestamp as relative time, localized to the app language
 * (e.g., "5 minutes ago" / "hace 5 minutos").
 */
export function formatDistanceToNow(timestamp: string | Date): string {
  const now = Date.now()
  const time = new Date(timestamp).getTime()
  const diffMs = now - time
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  // numeric:'auto' yields "now"/"ahora" for 0 and "N minutes ago"/"hace N minutos" for past values.
  const relativeTimeFormatter = new Intl.RelativeTimeFormat(appLocale(), { numeric: 'auto' })

  // Negative values = in the past. Same bucket thresholds as before.
  if (diffMins < 1) return relativeTimeFormatter.format(0, 'second')
  if (diffMins < 60) return relativeTimeFormatter.format(-diffMins, 'minute')
  if (diffHours < 24) return relativeTimeFormatter.format(-diffHours, 'hour')
  return relativeTimeFormatter.format(-diffDays, 'day')
}

/**
 * Format a date to a localized string
 */
export function format(timestamp: string | Date, formatStr?: string): string {
  const date = new Date(timestamp)

  if (formatStr === 'HH:mm' || formatStr === 'HH:mm:ss') {
    return formatDateInProfileZone(date, appLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      second: formatStr === 'HH:mm:ss' ? '2-digit' : undefined,
    })
  }

  if (formatStr === 'yyyy-MM-dd') {
    const clock = wallClockInZone(date, userConfigService.getUserTimezone())
    if (!clock) return ''
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${clock.year}-${pad(clock.month)}-${pad(clock.day)}`
  }

  if (formatStr === 'PP') {
    return formatDateInProfileZone(date, appLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // PPp format: date with time (same as PP but explicit about including time)
  if (formatStr === 'PPp') {
    return formatDateInProfileZone(date, appLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return formatDateInProfileZone(date, appLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Calculate difference in milliseconds between two dates
 */
export function differenceInMilliseconds(
  dateLeft: string | Date,
  dateRight: string | Date,
): number {
  return new Date(dateLeft).getTime() - new Date(dateRight).getTime()
}

/**
 * Format a date to ISO string
 */
export function formatISO(timestamp: string | Date): string {
  return new Date(timestamp).toISOString()
}

/**
 * Parse a date string or return current date
 */
export function parseDate(timestamp?: string | Date): Date {
  return timestamp ? new Date(timestamp) : new Date()
}

/**
 * Check if a date is valid
 */
export function isValidDate(timestamp: string | Date): boolean {
  const date = new Date(timestamp)
  return date instanceof Date && !isNaN(date.getTime())
}

/**
 * `Intl.DateTimeFormat` in the APP language (not the browser's, and never a
 * hard-coded 'en-US'). For chart axes and short labels that need month names —
 * absolute dates in tables go through `userConfigService.formatDate`, which
 * honours the user's date-format preference.
 */
export function formatInAppLanguage(
  timestamp: string | Date,
  options: Intl.DateTimeFormatOptions,
): string {
  return formatDateInProfileZone(timestamp, appLocale(), options)
}
